import { CCApiService } from './api-service';
import { UIConfig } from './config-service';
import type { CreateTemplateBody } from '../utils/app-studio-clone';

export type TemplateType = 'SEARCH' | 'TYPEAHEAD' | 'PRODUCT_QA' | 'CONVERSATIONAL_QA' | 'CONVERSATIONAL_QA_FAQ';

/**
 * Token placeholder on a TEXT, IMAGE or BUTTON component. TEXT and IMAGE resolve the token
 * against its own `datastoreQueryId`; BUTTON resolves against the component's query.
 */
export interface TemplateComponentToken {
  datastoreQueryId?: string;
  tokenName?: string;
  tokenValue?: string;
  /** Field the token reads; a BUTTON uses it for the first result's field, and TEXT tokens carry the key too. */
  referencedFieldName?: string;
  [key: string]: unknown;
}

/**
 * Lean TemplateComponent shell returned by the metadata API.
 * The runtime JSON (from getTemplate / getCompositionDetail) carries additional
 * per-type nested config that is not modelled in the API type. The optional fields
 * below capture the most common nested collections so that counts and the raw JSON
 * serialization in the component-detail-panel include the full configuration.
 */
export interface TemplateComponent {
  id: string;
  name: string;
  type: string;
  datastoreQueryId?: string;
  /** ID of another component within the same template that this component references. */
  referencedComponentId?: string;
  /** ID of a typeahead template used by a searchbox component. */
  typeaheadTemplateId?: string;
  /** ID of a searchahead template used by a searchbox component. */
  searchaheadTemplateId?: string;
  /** ID of an agent configuration used by an AI component. */
  agentConfigurationId?: string;
  /** ResultsList / Card-based components: array of card configurations. */
  cards?: unknown[];
  /** Facets component: array of facet configurations. */
  facets?: unknown[];
  /** Token placeholders (TEXT, IMAGE, BUTTON). */
  tokens?: TemplateComponentToken[];
  /** Arbitrary layout config blob; presence indicates layout is configured. */
  componentLayoutConfig?: Record<string, unknown>;
}

export interface TemplateDatastoreQuery {
  id: string;
  name: string;
  datastoreId: string;
  prefix?: string;
}

export interface TemplateSummary {
  id: string;
  /** Groups all versions of the same template. Use as the stable identifier for navigation. */
  initialId: string;
  name: string;
  type: TemplateType;
  versionNumber: number;
  published: boolean;
  changeMetadata: string | null;
  lastPublished: string | null;
  lastPublishedBy: string | null;
  /** Real server metadata fields returned by the list endpoint. */
  creationDate?: string | null;
  createdBy?: string | null;
  lastModified?: string | null;
  lastModifiedBy?: string | null;
  restored?: boolean;
  components?: TemplateComponent[];
  datastoreQueries?: TemplateDatastoreQuery[];
  /** Arbitrary layout JSON carried by the builder — omitted by older list endpoints. */
  layout?: Record<string, unknown>;
  /** Field-extraction config; returned by getTemplate, omitted by the list endpoint. */
  extraction?: Record<string, unknown>;
}

export interface AssetSummary {
  id: string;
  name: string;
  mimeType: string;
  publicUrl: string;
}

export interface DefaultCompositionSummary {
  /** Initial id of the composition a request to the application's bare host redirects to; null when there is none. */
  compositionId: string | null;
}

export interface CompositionSummary {
  id: string;
  /** Groups all versions of the same composition. Use as the stable identifier for navigation. */
  initialId: string;
  name: string;
  description: string;
  published: boolean;
  versionNumber: number;
  lastPublished: string | null;
  lastPublishedBy: string | null;
  uiAddress: string | null;
  hostProvider: string | null;
  /** URL path segment of the published composition, e.g. {urlSlug}.ui.{domain}/{pathSlug}. */
  pathSlug: string | null;
  /** Stored host of the active publish, `<slug>.ui.<domain>/<pathSlug>`; null when this version isn't published. */
  publishedHost?: string | null;
  /** Dead column, always null. Kept for the clone/export bundle format; use pathSlug for anything new. */
  publishedPath: string | null;
  logo: AssetSummary | null;
  favicon: AssetSummary | null;
  creationDate: string | null;
  createdBy: string | null;
  lastModified: string | null;
  lastModifiedBy: string | null;
  /** Short description of what changed in this version (commit-message style). */
  changeMetadata: string | null;
}

export interface TemplateUsageInfo {
  compositionInitialId: string;
  compositionLatestName: string;
  usedInLatestVersion: boolean;
  usedInPublishedVersion: boolean;
  usedAsReferencedTemplate: boolean;
}

export interface TemplateVersionUsageInfo {
  compositionId: string;
  compositionInitialId: string;
  compositionName: string;
  compositionVersionNumber: number;
  isPublished: boolean;
  isLatest: boolean;
  usedDirectly: boolean;
  usedAsReferencedTemplate: boolean;
}

export interface TemplateReferenceInfo {
  templateId: string;
  templateInitialId: string;
  templateName: string;
  templateVersionNumber: number;
  isPublished: boolean;
  isLatest: boolean;
  hasTypeaheadReference: boolean;
  hasSearchaheadReference: boolean;
  componentNames: string[];
}

export interface DatastoreTemplateUsageInfo {
  templateInitialId: string;
  templateLatestName: string;
  // Jackson serializes the Java isPublished()/isLatest() boolean getters without the `is` prefix.
  published: boolean;
  latest: boolean;
  // Omitted by the backend when the template is on no current composition page.
  usageInfo?: TemplateUsageInfo[];
}

export interface CompositionPage {
  id: string;
  name: string;
  url: string;
  homePage: boolean;
  template: TemplateSummary;
  /** Nested child pages (recursive). */
  subpages?: CompositionPage[];
}

export type OidcClientAuthMethod = 'CLIENT_SECRET_BASIC' | 'NONE';

/**
 * Absent, null or unknown values mean CLIENT_SECRET_BASIC.
 * @param oidc OIDC settings that may carry a clientAuthMethod.
 * @returns The effective client authentication method.
 */
export function resolveClientAuthMethod(oidc?: { clientAuthMethod?: string | null } | null): OidcClientAuthMethod {
  return oidc?.clientAuthMethod === 'NONE' ? 'NONE' : 'CLIENT_SECRET_BASIC';
}

export interface CompositionOidcSettings {
  clientId: string;
  issuerUri: string;
  authorizationUri: string;
  tokenUri: string;
  jwksUri: string;
  userinfoUri: string | null;
  endSessionEndpoint: string | null;
  scopes: string[];
  clientAuthMethod?: OidcClientAuthMethod | null;
  clientSecretRef?: string;
}

/** GET response shape from getCompositionDetail (AuthenticationSettingsSummary) */
export interface CompositionAuthSettingsSummary {
  method?: 'SINGLE_SIGN_ON';
  enabled?: boolean;
  sso?: {
    protocol?: 'OIDC';
    oidc?: CompositionOidcSettings;
  };
}

/** PUT request shape for updateCompositionBuilder (AuthenticationSettingsRequest) */
export interface CompositionAuthSettings {
  /** Java AuthenticationMethod enum only has SINGLE_SIGN_ON — auth is disabled via enabled: false, not by changing method */
  method?: 'SINGLE_SIGN_ON';
  enabled: boolean;
  sso?: {
    protocol: 'OIDC';
    oidc: {
      clientId: string;
      issuerUri: string;
      scopes: string[]; // backend deserializes into List<String>; a space-separated string fails with a 400
      clientAuthMethod?: OidcClientAuthMethod;
      clientSecret?: string;
      clientSecretRef?: string;
    };
  };
}

export interface CompositionDatastore {
  id: string;
  name: string;
  type: string;
  lastModified?: string;
  lastModifiedBy?: string;
  creationDate?: string;
}

export type DatastoreType = 'FUSION_QUERY_PROFILE' | 'QA_PAIR' | 'SEARCH_SERVICE' | 'TYPEAHEAD_SERVICE';

export interface Datastore {
  id: string;
  name: string;
  type: string;
  description?: string;
  // Type-specific fields are returned FLAT on the polymorphic datastore summary
  // (keyed by `type`), not nested under `config`. See DatastoreRequest for which
  // field each type uses.
  integratedSystemId?: string; // FUSION_QUERY_PROFILE
  queryProfileName?: string; // FUSION_QUERY_PROFILE
  appName?: string; // FUSION_QUERY_PROFILE
  userParameterName?: string; // FUSION_QUERY_PROFILE
  signalsQueryProfile?: string; // FUSION_QUERY_PROFILE
  indexTargetId?: string; // QA_PAIR
  searchServiceId?: string; // SEARCH_SERVICE
  typeaheadServiceId?: string; // TYPEAHEAD_SERVICE
  // Nested objects returned by GET for SEARCH_SERVICE / TYPEAHEAD_SERVICE types.
  searchService?: { id: string; name: string };
  typeaheadService?: { id: string; name: string };
  // Legacy/fallback only: current backend returns ids flat (above), not nested here.
  config?: Record<string, unknown>;
}

/**
 * Create/update payload for a datastore. Only the fields relevant to the chosen
 * `type` are sent; see DATASTORE_TYPE_REGISTRY in datastore-modal for which
 * fields each type requires.
 */
export interface DatastoreRequest {
  name: string;
  type: string;
  integratedSystemId?: string; // FUSION_QUERY_PROFILE
  queryProfileName?: string; // FUSION_QUERY_PROFILE
  appName?: string; // FUSION_QUERY_PROFILE
  userParameterName?: string; // FUSION_QUERY_PROFILE
  signalsQueryProfile?: string; // FUSION_QUERY_PROFILE
  indexTargetId?: string; // QA_PAIR
  searchServiceId?: string; // SEARCH_SERVICE
  typeaheadServiceId?: string; // TYPEAHEAD_SERVICE
}

export interface CompositionBuilderSummary {
  /** Version-specific ID of the composition returned by the builder update — use this to track the new version */
  id: string;
  initialId: string;
  name: string;
  description: string | null;
  versionNumber: number;
  published: boolean;
  /** URL path segment of the published composition, e.g. {urlSlug}.ui.{domain}/{pathSlug}. */
  pathSlug: string | null;
  /** Stored host of the active publish, `<slug>.ui.<domain>/<pathSlug>`; null when this version isn't published. */
  publishedHost?: string | null;
  /** Dead column, always null. Kept for the clone/export bundle format; use pathSlug for anything new. */
  publishedPath: string | null;
  pages: CompositionPage[];
  /** GET response shape — use CompositionAuthSettings for the PUT payload */
  authSettings: CompositionAuthSettingsSummary | null;
  /** Externally hosted UI address (cleared on clone). */
  uiAddress?: string | null;
  /** Host provider binding (cleared on clone). */
  hostProvider?: string | null;
  /** Asset ID for the composition logo (dropped on clone). */
  logoId?: string | null;
  /** Asset ID for the composition favicon (dropped on clone). */
  faviconId?: string | null;
  /** Signal store ID (dropped on clone). */
  signalStoreId?: string | null;
}

export class AppStudioApiService {
  static ApiService: CCApiService;
  static appStudioEnv: string;

  static init(config: UIConfig) {
    this.ApiService = new CCApiService(config.authClientId, config.authIssuerUri);
    // Derive env from the TLD of the apiBasePath hostname (e.g. internal.lucidworks.dev → 'dev', internal.lucidworks.com → 'com')
    const match = /lucidworks\.(\w+)/.exec(config.apiBasePath);
    this.appStudioEnv = match ? match[1] : 'com';
  }

  static getBaseUrl(appId: string): string {
    return `https://${appId}.applications.lucidworks.${this.appStudioEnv}`;
  }

  static async getDefaultComposition(appId: string): Promise<DefaultCompositionSummary> {
    return this.ApiService.performFetch<DefaultCompositionSummary>(`${this.getBaseUrl(appId)}/composition-default`);
  }

  static async setDefaultComposition(appId: string, compositionId: string | null): Promise<DefaultCompositionSummary> {
    return this.ApiService.performFetch<DefaultCompositionSummary>(`${this.getBaseUrl(appId)}/composition-default`, {
      method: 'PUT',
      body: JSON.stringify({ compositionId }),
    });
  }

  static async getCompositions(appId: string): Promise<CompositionSummary[]> {
    // Returns one entry per composition group (the latest version of each)
    return this.ApiService.performFetch<CompositionSummary[]>(`${this.getBaseUrl(appId)}/compositions`);
  }

  /**
   * Checks whether a path slug is free in this application, case-insensitively.
   * Pass initialId (the composition group being edited) so its own slug isn't flagged as a conflict.
   * @param appId
   * @param pathSlug
   * @param initialId
   */
  static async checkPathSlugAvailability(
    appId: string,
    pathSlug: string,
    initialId?: string
  ): Promise<{ pathSlug: string; available: boolean }> {
    const params = new URLSearchParams(initialId ? { pathSlug, initialId } : { pathSlug });
    return this.ApiService.performFetch<{ pathSlug: string; available: boolean }>(
      `${this.getBaseUrl(appId)}/compositions/path-slug-available?${params}`
    );
  }

  static async getCompositionHistory(appId: string, initialId: string): Promise<CompositionSummary[]> {
    const result = await this.ApiService.performFetch<{ content: CompositionSummary[] }>(
      `${this.getBaseUrl(appId)}/compositions/${initialId}/history`
    );
    // The backend orders history by creation timestamp, not version number, and ties in
    // creation timestamp can leave that order not matching version-number order at all.
    // Sort explicitly instead of trusting the API's order.
    return (result.content ?? []).slice().sort((a, b) => a.versionNumber - b.versionNumber);
  }

  static async getCompositionLatest(appId: string, initialId: string): Promise<CompositionSummary> {
    return this.ApiService.performFetch<CompositionSummary>(
      `${this.getBaseUrl(appId)}/compositions/${initialId}/latest`
    );
  }

  static async getCompositionPublished(appId: string, initialId: string): Promise<CompositionSummary> {
    return this.ApiService.performFetch<CompositionSummary>(
      `${this.getBaseUrl(appId)}/compositions/${initialId}/published`
    );
  }

  /**
   * versionId is the version-specific id (not initialId) — returns builder state for that exact version
   * @param appId
   * @param versionId
   */
  static async getCompositionDetail(appId: string, versionId: string): Promise<CompositionBuilderSummary> {
    return this.ApiService.performFetch<CompositionBuilderSummary>(
      `${this.getBaseUrl(appId)}/compositions/${versionId}/builder`
    );
  }

  static async publishComposition(
    appId: string,
    versionId: string,
    // The backend builds the published host itself, from the application's urlSlug and the
    // composition's pathSlug — nothing about the URL is sent from here.
    payload: { title: string; description?: string }
  ): Promise<void> {
    // eslint-disable-next-line sonarjs/no-invalid-await
    await this.ApiService.performFetch(`${this.getBaseUrl(appId)}/compositions/${versionId}/publish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async uploadAsset(appId: string, file: File): Promise<AssetSummary> {
    const form = new FormData();
    form.append('file', file);
    return this.ApiService.performFetch<AssetSummary>(`${this.getBaseUrl(appId)}/assets/upload`, {
      method: 'POST',
      body: form,
    });
  }

  static async createComposition(
    appId: string,
    // Note: authSettings cannot be set on creation — use updateComposition after creation
    payload: {
      name: string;
      description?: string;
      pathSlug?: string;
      /** Dead column on the backend. Kept for the clone/export bundle format; use pathSlug for anything new. */
      publishedPath?: string;
      logoId?: string;
      faviconId?: string;
    }
  ): Promise<CompositionSummary> {
    return this.ApiService.performFetch<CompositionSummary>(`${this.getBaseUrl(appId)}/compositions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async updateComposition(
    appId: string,
    versionId: string,
    // PUT /compositions/{id} updates metadata (name/description/changeMetadata) for an
    // existing version. initialId (the composition group id) and versionNumber are
    // REQUIRED — the backend validates that the body's initialId matches the path
    // version's group, so omitting it is rejected as a "Version initialId mismatch".
    // Use updateCompositionBuilder for logo, favicon, and authSettings.
    payload: {
      initialId: string;
      versionNumber: number;
      name: string;
      description?: string;
      changeMetadata?: string;
      pathSlug?: string;
    }
  ): Promise<CompositionSummary> {
    return this.ApiService.performFetch<CompositionSummary>(`${this.getBaseUrl(appId)}/compositions/${versionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // PUT /compositions/{id}/builder — supports authSettings, logoId, faviconId, pathSlug, pages
  static async updateCompositionBuilder(
    appId: string,
    versionId: string,
    payload: {
      /** REQUIRED: composition group identifier — without this the backend creates a new composition at v0 */
      initialId: string;
      /** REQUIRED: current version number — backend sets new version to versionNumber+1 */
      versionNumber: number;
      name?: string;
      description?: string;
      changeMetadata?: string;
      pathSlug?: string;
      /** Dead column on the backend. Kept for the clone/export bundle format; use pathSlug for anything new. */
      publishedPath?: string;
      logoId?: string;
      faviconId?: string;
      authSettings?: CompositionAuthSettings;
      /** The backend builds the new version from this request, so omitting it clears the signal store. */
      signalStoreId?: string;
      pages?: unknown[];
    }
  ): Promise<CompositionBuilderSummary> {
    return this.ApiService.performFetch<CompositionBuilderSummary>(
      `${this.getBaseUrl(appId)}/compositions/${versionId}/builder`,
      { method: 'PUT', body: JSON.stringify(payload) }
    );
  }

  static async deleteComposition(appId: string, versionId: string): Promise<void> {
    await this.ApiService.performFetch(`${this.getBaseUrl(appId)}/compositions/${versionId}`, {
      method: 'DELETE',
    });
  }

  static async getDatastores(appId: string): Promise<CompositionDatastore[]> {
    return this.ApiService.performFetch<CompositionDatastore[]>(`${this.getBaseUrl(appId)}/datastores`);
  }

  static async getDatastore(appId: string, datastoreId: string): Promise<Datastore> {
    return this.ApiService.performFetch<Datastore>(`${this.getBaseUrl(appId)}/datastores/${datastoreId}`);
  }

  static async getTemplatesUsingDatastore(appId: string, datastoreId: string): Promise<DatastoreTemplateUsageInfo[]> {
    return this.ApiService.performFetch<DatastoreTemplateUsageInfo[]>(
      `${this.getBaseUrl(appId)}/datastores/${datastoreId}/templates`
    );
  }

  static async createDatastore(appId: string, payload: DatastoreRequest): Promise<Datastore> {
    return this.ApiService.performFetch<Datastore>(`${this.getBaseUrl(appId)}/datastores`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async updateDatastore(appId: string, datastoreId: string, payload: DatastoreRequest): Promise<Datastore> {
    return this.ApiService.performFetch<Datastore>(`${this.getBaseUrl(appId)}/datastores/${datastoreId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async deleteDatastore(appId: string, datastoreId: string): Promise<void> {
    await this.ApiService.performFetch(`${this.getBaseUrl(appId)}/datastores/${datastoreId}`, {
      method: 'DELETE',
    });
  }

  // Returns all templates; filter by ?unassociated=true tracked in KUMO-1291 backend story
  static async getTemplates(appId: string): Promise<TemplateSummary[]> {
    return this.ApiService.performFetch<TemplateSummary[]>(`${this.getBaseUrl(appId)}/templates`);
  }

  static async getTemplate(appId: string, templateId: string): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates/${templateId}`);
  }

  static async getTemplateLatest(appId: string, initialId: string): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates/${initialId}/latest`);
  }

  static async getTemplateHistory(appId: string, initialId: string): Promise<TemplateSummary[]> {
    const result = await this.ApiService.performFetch<{ content: TemplateSummary[] }>(
      `${this.getBaseUrl(appId)}/templates/${initialId}/history`
    );
    // The backend orders history by creation timestamp, not version number, and ties in
    // creation timestamp can leave that order not matching version-number order at all.
    // Sort explicitly instead of trusting the API's order.
    return (result.content ?? []).slice().sort((a, b) => a.versionNumber - b.versionNumber);
  }

  static async createTemplate(
    appId: string,
    payload: {
      name: string;
      type: TemplateType;
      datastoreQueries?: { name: string; datastoreId: string }[];
      layout?: Record<string, unknown>;
    }
  ): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async createTemplateFull(appId: string, body: CreateTemplateBody): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateTemplateMetadata(
    appId: string,
    templateId: string,
    // PUT /templates/{id} versions the template by group. initialId (the template group id) and
    // versionNumber are REQUIRED — omitting initialId causes the backend to create a brand-new
    // template instead of versioning the existing one. Use changeMetadata and datastoreQueries
    // as needed; they remain optional.
    payload: {
      name: string;
      type: TemplateType;
      /** REQUIRED: template group identifier — omitting this causes the backend to create a brand-new template instead of versioning the existing one. */
      initialId: string;
      /** REQUIRED: current version number — backend increments this to set the new versionNumber. */
      versionNumber: number;
      /** Human-readable description of what changed in this version. */
      changeMetadata?: string;
      datastoreQueries?: { id?: string; name: string; prefix?: string; datastoreId: string }[];
      layout?: Record<string, unknown>;
      components?: TemplateComponent[];
      extraction?: Record<string, unknown>;
    }
  ): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Replaces all content of an existing template in place (layout, extraction, datastoreQueries, components).
   * All fields in the request replace the existing template configuration.
   * PUT /templates/{templateId}
   * @param appId
   * @param templateId
   * @param body
   */
  static async updateTemplateContent(
    appId: string,
    templateId: string,
    body: Record<string, unknown>
  ): Promise<TemplateSummary> {
    return this.ApiService.performFetch<TemplateSummary>(`${this.getBaseUrl(appId)}/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async getTemplateVersionUsage(appId: string, templateId: string): Promise<TemplateVersionUsageInfo[]> {
    return this.ApiService.performFetch<TemplateVersionUsageInfo[]>(
      `${this.getBaseUrl(appId)}/templates/${templateId}/version-usage`
    );
  }

  static async getTemplateGroupUsage(appId: string, templateId: string): Promise<TemplateUsageInfo[]> {
    return this.ApiService.performFetch<TemplateUsageInfo[]>(
      `${this.getBaseUrl(appId)}/templates/${templateId}/group-usage`
    );
  }

  static async getTemplateReferences(appId: string, templateId: string): Promise<TemplateReferenceInfo[]> {
    return this.ApiService.performFetch<TemplateReferenceInfo[]>(
      `${this.getBaseUrl(appId)}/templates/${templateId}/references`
    );
  }

  static async deleteTemplate(appId: string, templateId: string): Promise<void> {
    await this.ApiService.performFetch(`${this.getBaseUrl(appId)}/templates/${templateId}`, {
      method: 'DELETE',
    });
  }
}
